import Multipoint from '@arcgis/core/geometry/Multipoint';
import Point from '@arcgis/core/geometry/Point';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import MapView from '@arcgis/core/views/MapView';
import * as esriGeometryEngine from '@arcgis/core/geometry/geometryEngine';
import Polyline from '@arcgis/core/geometry/Polyline';
import Graphic from '@arcgis/core/Graphic';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import Polygon from '@arcgis/core/geometry/Polygon';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';

import { MapViewInputTask } from 'cd-eft/components/Design/BreaklineTools/framework/MapViewInputTask';
import { SurfaceDef } from 'cd-eft/components/Design/BreaklineTools/model/SurfaceDef';
import { GroundPoint } from 'cd-eft/components/Design/BreaklineTools/model/GroundPoint';
import { SurfaceBorder } from 'cd-eft/components/Design/BreaklineTools/model/SurfaceBorder';
import stores from '../../../../models/Stores';

const setStore = stores.AppStore.surveyEngineeringStore;

const __resultLineSymbol = new SimpleLineSymbol({ color: 'blue', width: 2 });
const __validNextPointSymbol = new SimpleMarkerSymbol({ color: 'green' });
const __validNextLineSymbol = new SimpleLineSymbol({ width: 2, color: 'green' });
const __invalidNextPointSymbol = new SimpleMarkerSymbol({ style: 'x', outline: { color: 'red' } });
const __invalidNextLineSymbol = new SimpleLineSymbol({ width: 2, color: 'red' });

export class InsertBorderPointsInputTask extends MapViewInputTask<SurfaceBorder> {
  private readonly surfaceDef: SurfaceDef;

  //setup state vars
  private redoStack: GroundPoint[] = [];
  private pointerMovePoint?: Point;
  private insertionPoints: GroundPoint[] = [];

  private allBorderPoints: GroundPoint[] = [];
  private allBorderPointsMP: Multipoint = new Multipoint();
  private allGroundPoints: GroundPoint[] = [];
  private allGroundPointsMP: Multipoint = new Multipoint();
  private nonBorderPoints: GroundPoint[] = [];
  private nonBorderPointsMP: Multipoint = new Multipoint();

  // private TEST_layer = new GraphicsLayer({ title: 'TEST_layer' });
  private feedbackLayer = new GraphicsLayer({ title: 'feedbackLayer' });
  private resultLayer = new GraphicsLayer({ title: 'resultLayer' });

  constructor(mapView: MapView, surfaceDef: SurfaceDef) {
    super(mapView);
    this.surfaceDef = surfaceDef;
  }

  //overridable lifecycle methods
  protected onSetup() {
    super.onSetup();

    this.redoStack = [];
    this.insertionPoints = [];
    this.pointerMovePoint = undefined;

    this.allBorderPoints = this.surfaceDef.border.borderPoints;
    this.allBorderPointsMP = new Multipoint({ spatialReference: this.mapView.spatialReference });
    for (const point of this.allBorderPoints) {
      this.allBorderPointsMP.addPoint(point.generateGeometry(this.mapView.spatialReference));
    }

    this.allGroundPoints = this.surfaceDef.getAllGroundPoints();
    this.allGroundPointsMP = new Multipoint({ spatialReference: this.mapView.spatialReference });
    for (const point of this.allGroundPoints) {
      this.allGroundPointsMP.addPoint(point.generateGeometry(this.mapView.spatialReference));
    }

    this.nonBorderPoints = [];
    for (const point of this.allGroundPoints) {
      if (!this.allBorderPoints.includes(point)) {
        this.nonBorderPoints.push(point);
      }
    }
    this.nonBorderPointsMP = new Multipoint({ spatialReference: this.mapView.spatialReference });
    for (const point of this.nonBorderPoints) {
      this.nonBorderPointsMP.addPoint(point.generateGeometry(this.mapView.spatialReference));
    }

    this.cursor = 'crosshair';
    this.mapView.map.add(this.feedbackLayer);
    this.mapView.map.add(this.resultLayer);
    // this.mapView.map.add(this.TEST_layer);

    //TEST
    const startPoint = this.surfaceDef.border.borderPoints[0];
    const endPoint = this.surfaceDef.border.borderPoints[this.surfaceDef.border.borderPoints.length - 1];
    const startGraphic = startPoint.generateGraphic(this.mapView.spatialReference);
    const endGraphic = endPoint.generateGraphic(this.mapView.spatialReference);
    startGraphic.symbol = new SimpleMarkerSymbol({ size: 5 });
    endGraphic.symbol = new SimpleMarkerSymbol({ size: 10 });

    // this.TEST_layer.add(startGraphic);
    // this.TEST_layer.add(endGraphic);
  }
  protected onCleanup() {
    super.onCleanup();

    this.mapView.map.remove(this.feedbackLayer);
    this.mapView.map.remove(this.resultLayer);
    // this.mapView.map.remove(this.TEST_layer);
    this.feedbackLayer.removeAll();
    this.resultLayer.removeAll();
    // this.TEST_layer.removeAll();

    this.redoStack = [];
    this.insertionPoints = [];

    this.allBorderPoints = [];
    this.allBorderPointsMP = new Multipoint();
    this.allGroundPoints = [];
    this.allGroundPointsMP = new Multipoint();
    this.nonBorderPoints = [];
    this.nonBorderPointsMP = new Multipoint();

    this.pointerMovePoint = undefined;
  }
  protected onPointerMove(e: __esri.ViewPointerMoveEvent): void {
    super.onPointerMove(e);
    this.pointerMovePoint = this.mapView.toMap(e);
    this.updateFeedbackLayer();
  }
  protected onImmediateClick(e: __esri.ViewImmediateClickEvent) {
    super.onImmediateClick(e);

    if (this.insertionPoints.length === 0) {
      const nextPoint = this.findNearestBorderPoint(e.mapPoint);
      const valid = this.validateNextPoint(nextPoint);
      if (!valid) {
        // ignore user input
        return; //short circuit
      }

      this.insertionPoints.push(nextPoint);

      // clear the redoStack, it is now invalidated
      this.redoStack.splice(0, this.redoStack.length);

      this.updateResultLayer();
      this.updateFeedbackLayer();
    } else if (this.insertionPoints.length === 1) {
      const nextPoint = this.findNearestNonBorderPoint(e.mapPoint);
      const valid = this.validateNextPoint(nextPoint);
      if (!valid) {
        // ignore user input
        return; //short circuit
      }

      this.insertionPoints.push(nextPoint);

      // clear the redoStack, it is now invalidated
      this.redoStack.splice(0, this.redoStack.length);

      this.updateResultLayer();
      this.updateFeedbackLayer();
    } else {
      // this.insertionPoints.length >= 2

      const nextPoint = this.findNearestGroundPoint(e.mapPoint);
      const valid = this.validateNextPoint(nextPoint);
      if (!valid) {
        // ignore user input
        return; //short circuit
      }

      this.insertionPoints.push(nextPoint);

      // clear the redoStack, it is now invalidated
      this.redoStack.splice(0, this.redoStack.length);

      this.updateResultLayer();
      this.updateFeedbackLayer();

      // determine if final point matches a border point
      if (this.surfaceDef.border.borderPoints.indexOf(nextPoint) >= 0) {
        const resultBorder = this.generateResultBorder(this.insertionPoints);
        this.completeNow(resultBorder);

        // this.surfaceDef.findGoundPointsTinnableModifiedByBorder(resultBorder.generatePolygonGeometry());
        this.surfaceDef.applyBorderOnSurveyEditables(resultBorder.generatePolygonGeometry(), resultBorder.generatePolylineGeometry());
        setStore.triggerSurfaceDesignerVm?.drawSurveyPoints();
        setStore.triggerBreaklineToolboxVM?.drawGroundPoints();

        return;
      }
    }
  }

  // protected onImmediateDoubleClick(e: __esri.ViewImmediateDoubleClickEvent) {
  //   super.onImmediateDoubleClick(e);
  // }

  protected async onKeyDown(e: __esri.ViewKeyDownEvent) {
    super.onKeyDown(e);

    if (e.key === 'z' || e.key === 'Z') {
      // undo last digitized point
      e.stopPropagation();
      if (this.insertionPoints.length > 0) {
        this.redoStack.push(this.insertionPoints.pop()!);
        this.updateResultLayer();
        this.updateFeedbackLayer();
      }
      return;
    }

    if (e.key === 'r' || e.key === 'R') {
      // redo last digitized point
      e.stopPropagation();
      if (this.redoStack.length > 0) {
        this.insertionPoints.push(this.redoStack.pop()!);
        this.updateResultLayer();
        this.updateFeedbackLayer();
      }
      return;
    }
  }

  private updateFeedbackLayer(): void {
    //clear the layer
    this.feedbackLayer.removeAll();
    if (!this.pointerMovePoint) {
      return;
    }

    if (this.insertionPoints.length === 0) {
      const nextPoint = this.findNearestBorderPoint(this.pointerMovePoint);
      const valid = this.validateNextPoint(nextPoint);

      // //construct the nextPoint graphic
      const nextPointGraphic = new Graphic({
        geometry: nextPoint.generateGeometry(this.mapView.spatialReference),
        symbol: valid ? __validNextPointSymbol : __invalidNextPointSymbol,
      });

      this.feedbackLayer.add(nextPointGraphic);
    } else if (this.insertionPoints.length === 1) {
      const nextPoint = this.findNearestNonBorderPoint(this.pointerMovePoint);
      const valid = this.validateNextPoint(nextPoint);

      // //construct the nextPoint graphic
      const nextPointGraphic = new Graphic({
        geometry: nextPoint.generateGeometry(this.mapView.spatialReference),
        symbol: valid ? __validNextPointSymbol : __invalidNextPointSymbol,
      });
      this.feedbackLayer.add(nextPointGraphic);

      if (this.insertionPoints.length > 0) {
        //construct the nextLine graphic
        const lastPoint = this.insertionPoints[this.insertionPoints.length - 1];
        const nextLine = new Polyline();
        nextLine.spatialReference = this.mapView.spatialReference;
        nextLine.hasZ = true;
        nextLine.addPath([
          lastPoint.generateGeometry(this.mapView.spatialReference),
          valid ? nextPoint.generateGeometry(this.mapView.spatialReference) : this.pointerMovePoint,
        ]);
        const nextLineGraphic = new Graphic();
        nextLineGraphic.geometry = nextLine;
        nextLineGraphic.symbol = valid ? __validNextLineSymbol : __invalidNextLineSymbol;
        this.feedbackLayer.add(nextLineGraphic);
      }
    } else {
      // this.insertionPoints.length >= 2

      const nextPoint = this.findNearestGroundPoint(this.pointerMovePoint);
      const valid = this.validateNextPoint(nextPoint);

      // //construct the nextPoint graphic
      const nextPointGraphic = new Graphic({
        geometry: nextPoint.generateGeometry(this.mapView.spatialReference),
        symbol: valid ? __validNextPointSymbol : __invalidNextPointSymbol,
      });
      this.feedbackLayer.add(nextPointGraphic);

      if (this.insertionPoints.length > 0) {
        //construct the nextLine graphic
        const lastPoint = this.insertionPoints[this.insertionPoints.length - 1];
        const nextLine = new Polyline();
        nextLine.spatialReference = this.mapView.spatialReference;
        nextLine.hasZ = true;
        nextLine.addPath([
          lastPoint.generateGeometry(this.mapView.spatialReference),
          valid ? nextPoint.generateGeometry(this.mapView.spatialReference) : this.pointerMovePoint,
        ]);
        const nextLineGraphic = new Graphic();
        nextLineGraphic.geometry = nextLine;
        nextLineGraphic.symbol = valid ? __validNextLineSymbol : __invalidNextLineSymbol;
        this.feedbackLayer.add(nextLineGraphic);
      }

      //generate the preview border result graphic
      if (valid && this.insertionPoints.length >= 2 && this.allBorderPoints.includes(nextPoint)) {
        const previewPoints: GroundPoint[] = [];
        for (const point of this.insertionPoints) {
          previewPoints.push(point);
        }
        previewPoints.push(nextPoint);

        const resultPreview = this.generateResultBorder(previewPoints);
        const ring: Point[] = [];
        for (const point of resultPreview.borderPoints) {
          ring.push(point.generateGeometry(this.mapView.spatialReference));
        }

        const polygon = new Polygon();
        polygon.spatialReference = this.mapView.spatialReference;
        polygon.hasZ = true;
        polygon.addRing(ring);

        const resultBorderGraphic = new Graphic();
        resultBorderGraphic.geometry = polygon;
        resultBorderGraphic.symbol = new SimpleFillSymbol();
        this.feedbackLayer.add(resultBorderGraphic);
      }
    }
  }

  private updateResultLayer() {
    //clear the layer
    this.resultLayer.removeAll();
    //reconstruct the line graphic
    if (this.insertionPoints.length > 1) {
      const path: Point[] = [];
      for (const point of this.insertionPoints) {
        path.push(point.generateGeometry(this.mapView.spatialReference));
      }
      const polyline = new Polyline({ spatialReference: this.mapView.spatialReference });
      polyline.hasZ = true;
      polyline.addPath(path);

      const graphic = new Graphic();
      graphic.geometry = polyline;
      graphic.symbol = __resultLineSymbol;
      this.resultLayer.add(graphic);
    }
  }

  private validateNextPoint(nextPoint: GroundPoint): boolean {
    //do not allow non-tinable points
    //if (!nextPoint.tinable) return false; //Commented the line of code to meet EFT-1014 Allow adding non-tinnable points to border

    //do not allow non-visible points
    if (!nextPoint.visible) return false;

    //cannot be a point already within insertion points
    if (this.insertionPoints.includes(nextPoint)) return false;

    //enforce rules for first point
    if (this.insertionPoints.length === 0) {
      //must be an existing border point
      if (!this.allBorderPoints.includes(nextPoint)) return false;
    }

    //enforce rules for second point
    if (this.insertionPoints.length === 1) {
      //must NOT be an existing border point
      if (!this.nonBorderPoints.includes(nextPoint)) return false;
    }

    //enforce rules for third point and beyond
    if (this.insertionPoints.length >= 2) {
      //must be an existing ground point
      if (!this.allGroundPoints.includes(nextPoint)) return false;
    }

    // cannot cause insertionPoints path to be self-interecting
    if (this.insertionPoints.length >= 2) {
      const path: Point[] = [];
      for (const point of this.insertionPoints) {
        path.push(point.generateGeometry(this.mapView.spatialReference));
      }
      const polyline = new Polyline();
      polyline.spatialReference = this.mapView.spatialReference;
      polyline.hasZ = true;
      polyline.addPath(path);

      const lastPoint = this.insertionPoints[this.insertionPoints.length - 1];
      const nextSegment = new Polyline();
      nextSegment.spatialReference = this.mapView.spatialReference;
      nextSegment.hasZ = true;
      nextSegment.addPath([
        lastPoint.generateGeometry(this.mapView.spatialReference),
        nextPoint.generateGeometry(this.mapView.spatialReference),
      ]);

      if (esriGeometryEngine.crosses(polyline, nextSegment)) return false;
    }

    // cannot cause final result to be self-intersecting
    if (this.insertionPoints.length >= 2 && this.allBorderPoints.includes(nextPoint)) {
      // console.log('FINAL TEST');
      const previewPoints: GroundPoint[] = [];
      for (const point of this.insertionPoints) {
        previewPoints.push(point);
      }
      previewPoints.push(nextPoint);

      const resultPreview = this.generateResultBorder(previewPoints);
      const ring: Point[] = [];
      for (const point of resultPreview.borderPoints) {
        ring.push(point.generateGeometry(this.mapView.spatialReference));
      }

      const polygon = new Polygon();
      polygon.spatialReference = this.mapView.spatialReference;
      polygon.hasZ = true;
      polygon.addRing(ring);

      if (polygon.isSelfIntersecting) return false;
    }

    return true;
  }

  private generateResultBorder(newSection: GroundPoint[]): SurfaceBorder {
    if (newSection.length < 3) throw new Error('Unexpected result. Less than 3.');

    const indexA = this.surfaceDef.border.borderPoints.indexOf(newSection[0]);
    const indexB = this.surfaceDef.border.borderPoints.indexOf(newSection[newSection.length - 1]);
    // console.log('indexA', indexA);
    // console.log('indexB', indexB);
    if (indexA === undefined || indexA === null || indexA < 0) throw new Error('Unexpected result.');
    if (indexB === undefined || indexB === null || indexB < 0) throw new Error('Unexpected result.');
    if (indexA === indexB) throw new Error('Unexpected result.');
    if (indexA === indexB) throw new Error('Unexpected result.');

    const points: GroundPoint[] = [];
    for (const point of newSection) {
      points.push(point);
    }
    let index1 = indexA;
    let index2 = indexB;
    if (indexA > indexB) {
      index1 = indexB;
      index2 = indexA;
      points.reverse();
      // console.warn('REVERSE 1');
    }
    //remove first and last points
    points.shift();
    points.pop();

    //determine longer remaining path
    const pathA: Point[] = [];
    for (let i = index2; i < this.surfaceDef.border.borderPoints.length; i++) {
      pathA.push(this.surfaceDef.border.borderPoints[i].generateGeometry(this.mapView.spatialReference));
    }
    for (let i = 0; i <= index1; i++) {
      pathA.push(this.surfaceDef.border.borderPoints[i].generateGeometry(this.mapView.spatialReference));
    }
    const pathB: Point[] = [];
    for (let i = index1; i <= index2; i++) {
      pathB.push(this.surfaceDef.border.borderPoints[i].generateGeometry(this.mapView.spatialReference));
    }
    const polylineA = new Polyline();
    polylineA.spatialReference = this.mapView.spatialReference;
    polylineA.hasZ = true;
    polylineA.addPath(pathA);
    const polylineB = new Polyline();
    polylineB.spatialReference = this.mapView.spatialReference;
    polylineB.hasZ = true;
    polylineB.addPath(pathB);
    const lengthA = esriGeometryEngine.planarLength(polylineA);
    const lengthB = esriGeometryEngine.planarLength(polylineB);
    // console.log('lengthA', lengthA);
    // console.log('lengthB', lengthB);

    const result = new SurfaceBorder();
    if (lengthA > lengthB) {
      for (let i = 0; i <= index1; i++) {
        result.borderPoints.push(this.surfaceDef.border.borderPoints[i]);
      }
      for (const point of points) {
        result.borderPoints.push(point);
      }
      for (let i = index2; i < this.surfaceDef.border.borderPoints.length; i++) {
        result.borderPoints.push(this.surfaceDef.border.borderPoints[i]);
      }
    } else {
      for (let i = index1; i <= index2; i++) {
        result.borderPoints.push(this.surfaceDef.border.borderPoints[i]);
      }
      points.reverse();
      // console.warn('REVERSE 2');
      for (const point of points) {
        result.borderPoints.push(point);
      }
    }

    result.wkid = this.surfaceDef.wkid;
    return result;
  }

  private findNearestBorderPoint(mapPoint: Point): GroundPoint {
    const findResult = esriGeometryEngine.nearestVertex(this.allBorderPointsMP, mapPoint);
    const result = this.allBorderPoints[findResult.vertexIndex];
    return result;
  }

  private findNearestNonBorderPoint(mapPoint: Point): GroundPoint {
    const findResult = esriGeometryEngine.nearestVertex(this.nonBorderPointsMP, mapPoint);
    const result = this.nonBorderPoints[findResult.vertexIndex];
    return result;
  }

  private findNearestGroundPoint(mapPoint: Point): GroundPoint {
    const findResult = esriGeometryEngine.nearestVertex(this.allGroundPointsMP, mapPoint);
    const result = this.allGroundPoints[findResult.vertexIndex];
    return result;
  }
}
